import { useState } from 'react';
import { ChevronDown, ChevronRight, Eye, Trash2 } from 'lucide-react';
import { Button, Input, Label, Textarea } from '@artstorefronts/ui';
import type { RegistryConfigField } from '../onboarding/StepConfigForm';
import { fieldText } from './draft';
import type { StepDraft } from './types';

/** Long copy needs room. Which keys those are comes from the registry's own maxLength. */
const MULTILINE_MIN_LENGTH = 200;

/**
 * Says what THIS position's button does, so an author cannot read the form as though every
 * step navigates. No step has a destination field at all; the hand-off is a wizard-level one.
 */
function buttonNote(isLast: boolean, handoffHref: string): string {
  if (!isLast) return 'This button advances to the next step. It never leaves the page.';
  if (handoffHref.trim()) {
    return `This is the last step, so this button finishes the wizard and closes it, then opens ${handoffHref.trim()}.`;
  }
  return 'This is the last step, so this button finishes the wizard and closes it.';
}

function ImageThumbnail({ url }: { url: string }) {
  return (
    <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50 text-[10px] text-gray-400">
      {url ? <img src={url} alt="" className="h-full w-full object-contain" /> : 'none'}
    </span>
  );
}

/**
 * Registry-driven: the fields, their labels, their help text and their limits all come from
 * `featureWizardStepRegistry`, so nothing here is a hardcoded list of a step's contents. Only
 * the two presentation touches the spec calls for are keyed by name — the thumbnail beside the
 * image URL, and the alt input that appears exactly when it becomes required.
 */
function StepFields({
  step,
  schema,
  isLast,
  handoffHref,
  onChange,
}: {
  step: StepDraft;
  schema: RegistryConfigField[];
  isLast: boolean;
  handoffHref: string;
  onChange: (config: Record<string, unknown>) => void;
}) {
  const imageUrl = fieldText(step.config, 'imageUrl').trim();

  return (
    <div className="space-y-4 border-t border-gray-100 pt-3">
      {schema.map((field) => {
        if (field.key === 'imageAlt' && !imageUrl) return null;

        const inputId = `${step.key}-${field.key}`;
        const value = fieldText(step.config, field.key);
        const set = (next: string) => onChange({ ...step.config, [field.key]: next });

        if (field.type !== 'text') {
          return (
            <p key={field.key} className="text-xs text-amber-700">
              Unsupported field type &quot;{field.type}&quot; for {field.key}
            </p>
          );
        }

        const isMultiline = (field.maxLength ?? 0) >= MULTILINE_MIN_LENGTH && field.key !== 'imageUrl';

        return (
          <div key={field.key}>
            <Label htmlFor={inputId} className="mb-1 block text-sm">
              {field.label}
            </Label>
            {field.description ? <p className="mb-1 text-xs text-gray-500">{field.description}</p> : null}

            {isMultiline && (
              <Textarea
                id={inputId}
                className="w-full text-sm"
                rows={3}
                maxLength={field.maxLength ?? undefined}
                value={value}
                onChange={(event) => set(event.target.value)}
              />
            )}

            {!isMultiline && field.key === 'imageUrl' && (
              <div className="flex items-start gap-3">
                <Input
                  id={inputId}
                  type="text"
                  className="min-w-0 flex-1 font-mono text-sm"
                  maxLength={field.maxLength ?? undefined}
                  value={value}
                  onChange={(event) => set(event.target.value)}
                />
                <ImageThumbnail url={imageUrl} />
              </div>
            )}

            {!isMultiline && field.key !== 'imageUrl' && (
              <Input
                id={inputId}
                type="text"
                className="w-full text-sm"
                maxLength={field.maxLength ?? undefined}
                value={value}
                onChange={(event) => set(event.target.value)}
              />
            )}

            {field.key === 'ctaLabel' ? (
              <p className="mt-1 text-xs text-gray-500">{buttonNote(isLast, handoffHref)}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * One step row. Its header is DERIVED — position plus that step's own headline — because asking
 * a teammate to name a step and then write its headline is the same sentence typed twice.
 */
export function StepCard({
  step,
  isLast,
  isPublished,
  canRemove,
  collapsed,
  schema,
  handoffHref,
  onToggle,
  onChange,
  onRemove,
  onPreview,
}: {
  step: StepDraft;
  isLast: boolean;
  isPublished: boolean;
  canRemove: boolean;
  collapsed: boolean;
  schema: RegistryConfigField[] | undefined;
  handoffHref: string;
  onToggle: () => void;
  onChange: (config: Record<string, unknown>) => void;
  onRemove: () => void;
  onPreview: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const headline = fieldText(step.config, 'headline').trim();

  const handleRemoveClick = () => {
    if (isPublished) {
      setConfirming(true);
      return;
    }
    onRemove();
  };

  return (
    <div className="mb-2 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? `Expand step ${step.position}` : `Collapse step ${step.position}`}
          className="shrink-0 text-gray-500"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
        <span className="shrink-0 rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-900">
          {step.position}
        </span>
        {headline ? (
          <span className="min-w-0 flex-1 truncate text-sm text-gray-700">{headline}</span>
        ) : (
          <span className="flex-1" />
        )}
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onPreview}
            aria-label={`Preview step ${step.position}`}
            title={`Preview step ${step.position}`}
            className="text-gray-400 hover:text-gray-700"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={handleRemoveClick}
            disabled={!canRemove}
            aria-label={`Remove step ${step.position}`}
            title={canRemove ? `Remove step ${step.position}` : 'A wizard needs at least one step'}
            className="text-gray-400 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {confirming && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
          <p className="text-sm font-semibold">Remove step {step.position}?</p>
          <p className="mt-1 text-xs leading-relaxed">
            This wizard is published. Per-step counts are keyed to position, so removing a step re-labels the historic
            step rows for this wizard — arrival, open, completion and dismissal counts are unaffected.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="font-normal text-red-700 hover:bg-red-50 hover:text-red-800"
              onClick={() => {
                setConfirming(false);
                onRemove();
              }}
            >
              Remove step
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="font-normal"
              onClick={() => setConfirming(false)}
            >
              Keep it
            </Button>
          </div>
        </div>
      )}

      {!collapsed && (
        <div className="mt-3">
          {schema ? (
            <StepFields step={step} schema={schema} isLast={isLast} handoffHref={handoffHref} onChange={onChange} />
          ) : (
            <p className="text-sm text-amber-600">Unknown step in registry: {step.stepCode}</p>
          )}
        </div>
      )}
    </div>
  );
}
